import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["modal", "title", "description", "accessLevelInput", "submitButton"]
  static values = { url: String }

  open(event) {
    event.preventDefault()

    const selectedIds = this.selectedIds()
    if (selectedIds.length === 0) return

    this.selectedCount = selectedIds.length
    this.resetForm()
    this.updateCopy(selectedIds.length)
    Modal.getOrCreateInstance(this.modalTarget).show()
    this.validate()
  }

  validate() {
    if (!this.hasSubmitButtonTarget) return

    const accessLevelSelected = this.accessLevelInputTargets.some((input) => input.checked)
    this.submitButtonTarget.disabled = !accessLevelSelected || this.submitButtonTarget.dataset.saving === "true"
  }

  async save(event) {
    event.preventDefault()

    const selectedIds = this.selectedIds()
    const accessLevel = this.selectedAccessLevel()
    if (selectedIds.length === 0 || !accessLevel || !this.hasUrlValue) return

    this.setSaving(true)

    const formData = new FormData()
    formData.append("_method", "PATCH")
    formData.append("access_level", accessLevel)
    selectedIds.forEach((id) => formData.append("company_user_ids[]", id))

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        data.updated.forEach((item) => {
          this.updateRowAccessLevel(item.id, item.access_level, item.access_level_label)
        })
        this.clearSelection()
        Modal.getOrCreateInstance(this.modalTarget).hide()
      } else {
        const message = data?.errors?.join(", ") || `HTTP ${response.status}`
        alert(`Failed to update access levels: ${message}`)
      }
    } catch (error) {
      console.error("Error bulk updating access levels:", error)
      alert("An error occurred. Please try again.")
    } finally {
      this.setSaving(false)
      this.validate()
    }
  }

  modalHidden() {
    this.resetForm()
    this.validate()
  }

  resetForm() {
    this.accessLevelInputTargets.forEach((input) => {
      input.checked = false
    })
  }

  updateCopy(count) {
    const noun = count === 1 ? "member" : "members"

    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `Edit access level for ${count} ${noun}`
    }

    if (this.hasDescriptionTarget) {
      this.descriptionTarget.textContent = `This will overwrite the access level for all ${count} ${noun}.`
    }

    if (this.hasSubmitButtonTarget && this.submitButtonTarget.dataset.saving !== "true") {
      this.submitButtonTarget.textContent = count === 1 ? "Update 1 member" : `Update ${count} members`
    }
  }

  selectedAccessLevel() {
    const selected = this.accessLevelInputTargets.find((input) => input.checked)
    return selected?.value
  }

  selectedIds() {
    return [...this.element.querySelectorAll('[data-bulk-select-target="checkbox"]:checked')]
      .filter((checkbox) => !checkbox.closest("tr[hidden]"))
      .map((checkbox) => checkbox.value)
  }

  clearSelection() {
    const bulkSelectElement = this.element.querySelector('[data-controller~="bulk-select"]')
    if (!bulkSelectElement) return

    const controller = this.application.getControllerForElementAndIdentifier(bulkSelectElement, "bulk-select")
    controller?.clearSelection()
  }

  updateRowAccessLevel(companyUserId, accessLevel, accessLevelLabel) {
    const checkbox = this.element.querySelector(
      `[data-bulk-select-target="checkbox"][value="${companyUserId}"]`
    )
    if (!checkbox) return

    const row = checkbox.closest("tr")
    const accessLevelCell = row?.querySelector('[data-sort-key="access_level"]')
    const field = accessLevelCell?.querySelector('[data-controller~="company-user-field"]')
    if (!accessLevelCell || !field) return

    accessLevelCell.dataset.sortValue = accessLevel
    accessLevelCell.dataset.filterValue = accessLevel.toLowerCase()

    const hidden = field.querySelector(".select-hidden-input-value")
    const label = field.querySelector("[data-app-select-target='label']")
    const options = field.querySelectorAll("[data-app-select-target='option']")

    if (hidden) hidden.value = accessLevel
    if (label) label.textContent = accessLevelLabel

    options.forEach((option) => {
      option.classList.toggle("app-select-option-selected", option.dataset.value === accessLevel)
    })

    const fieldController = this.application.getControllerForElementAndIdentifier(field, "company-user-field")
    if (fieldController) fieldController.previousValue = accessLevel
  }

  setSaving(saving) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.dataset.saving = saving ? "true" : "false"

    if (saving) {
      this.submitButtonTarget.textContent = "Saving..."
    } else {
      this.updateCopy(this.selectedCount)
    }

    this.submitButtonTarget.disabled = saving || !this.selectedAccessLevel()
  }
}
